import React from 'react';
import { isEqual } from 'lodash-es';

import { useAppDispatch, useAppSelector } from '@/store';
import { userBrandsList } from '@/store/slices/casino-instance';

import { SideMenu } from '../MainLayout/components/SideMenu';
import { useAuthGate } from '../useAuthGate';
import { Root, SideMenuCol, Content, CustomizerBackdrop } from './CustomizerLayout.styles';

export type CustomizerLayoutProps = {
  children: React.ReactNode;
};

/**
 * Chrome for the customizer wizard ("Take your website live").
 *
 * Keeps the app's left side rail (so the wizard sits inside the account shell —
 * the desktop Figma shows it) but drops the standard top bar + Lumio column: the
 * wizard renders its OWN header (step title + Ask Lumio) and right progress panel.
 * Owns the auth gate the config pages used to provide. On the mobile flow the rail
 * is hidden and the wizard fills the viewport with its own chrome.
 *
 * While the owner has no OTHER brand to navigate to, the rail is rendered LOCKED
 * (every entry inert + frosted under a padlock, labels dimmed, logo untouched) —
 * its destinations (Home / Stats / …) would be empty until this first brand goes
 * live. It unlocks once a returning owner with another brand is detected. The brand
 * the wizard is building is session-based and only joins `casinoInstance.brands`
 * once deployed (matched by its deploy `session_id`), so before then the list holds
 * only the owner's other brands — the same signal PaymentSuccess uses.
 */
export const CustomizerLayout = ({ children }: CustomizerLayoutProps): React.ReactElement | null => {
  // Shared auth gate (same rule as MainLayout) — see ../useAuthGate.
  const { authenticated, gate } = useAuthGate();

  const dispatch = useAppDispatch();
  const brands = useAppSelector((state) => state.casinoInstance.brands, isEqual);
  const brandsLoaded = useAppSelector((state) => state.casinoInstance.loaded);
  const brandsLoading = useAppSelector((state) => state.casinoInstance.loading);
  const sessionId = useAppSelector((state) => state.session.sessionId);

  // Self-fetch once if we arrived cold (e.g. a post-payment redirect that skipped
  // the app-wide sign-in load); no-op once loaded/loading. Same guarded one-shot as
  // PaymentSuccess / casino-settings.
  const requestedBrands = React.useRef(false);
  React.useEffect(() => {
    if (requestedBrands.current || !authenticated) return;
    requestedBrands.current = true;
    if (!brandsLoaded && !brandsLoading) dispatch(userBrandsList());
  }, [authenticated, brandsLoaded, brandsLoading, dispatch]);

  // "Other brands" = every brand except the one this wizard is building (matched by
  // its deploy session id; before deploy that brand isn't in the list at all). Lock
  // the rail until we've LOADED the list and confirmed another brand exists — so a
  // first-time owner never sees the rail flash from interactive to locked.
  const hasOtherBrands = brands.some(
    (brand) => !brand.deployments?.some((deployment) => deployment.session_id === sessionId),
  );
  const locked = !(brandsLoaded && hasOtherBrands);

  if (gate !== undefined) return gate;

  return (
    <Root>
      {/* Paint the whole document the themed wizard page background while the
          customizer is mounted (so the config shell's dark body never shows
          behind/around the wizard). Dark is unchanged; light makes it off-white. */}
      <CustomizerBackdrop />
      <SideMenuCol>
        <SideMenu locked={locked} />
      </SideMenuCol>
      <Content>{children}</Content>
    </Root>
  );
};

export default CustomizerLayout;